import classNames from 'classnames/bind';
import TrackingEvent from 'constants/trackingEvents';
import { a11yOnClick } from 'helpers/interactions';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PostBody } from 'types/post';
import { goToPostPage } from 'utils/routerHelpers';

import React from 'react';

import { Tooltip } from '@thinkific/toga-react';

import { useCommunityContext } from 'components/CommunityContext';
import UserAvatar from 'components/UserAvatar';
import Content from 'components/new/Posts/ReadOnlyContent/Content';
import { useGlobalState } from 'components/new/providers/GlobalStateProvider';

import styles from './TrendingPost.module.scss';

const cx = classNames.bind(styles);

interface TrendingPostProps {
  author: {
    fullName: string | null;
    id: string;
    avatarUrl: string | null;
  } | null;
  commentCount: number;
  body: PostBody;
  id: string;
}

const TrendingPost = ({ author, commentCount, body, id }: TrendingPostProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { community, trackEvent } = useCommunityContext();
  const { expandCollapseContent } = useGlobalState();

  const handlePostClick = () => {
    expandCollapseContent(id);
    goToPostPage(router, community.id, id);

    trackEvent(TrackingEvent.TRENDING_POST_CLICKED, {});
  };

  return (
    <li
      aria-label={t('shared-read_full_post', 'Read full post: {{title}}', {
        title: body.plainText?.slice(0, 100) || '',
      })}
      className={cx('trending-post__item')}
      data-qa="trending-post__item"
      {...a11yOnClick<HTMLLIElement>(() => handlePostClick())}
    >
      <Tooltip title={author?.fullName}>
        <UserAvatar
          avatarUrl={author?.avatarUrl}
          className={cx('trending-post__avatar')}
          size="xs"
          userId={author?.id || ''}
          username={author?.fullName || ''}
        />
      </Tooltip>
      <div className={cx('trending-post__item__post')}>
        <span data-qa="trending-post-title">
          <Content
            body={body}
            containerClassName={cx('trending-post__item__post--title')}
            editorClassName={cx('trending-post__item__post-content')}
            id={id}
            showFullContentToggle={false}
          />
        </span>
        <span className={cx('trending-post__item__post--subtitle')} data-qa="trending-post-comment">
          {t('shared-comment_amount', '{{count}} comment', {
            count: commentCount,
          })}
        </span>
      </div>
    </li>
  );
};

export default TrendingPost;
